import { ClinicCreateFormData } from '@/pages/admin/site-admin-clinic-info/clinicFormSchema';
import dayjs, { Dayjs } from '@/shared/lib/dayjs';
import { Radio } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import { AppAdminFormLabel, AppTextArea } from '@components/atoms';
import { TextFieldWithLabel } from '@components/molecules';
import { RA2NDatePicker } from '@components/organisms';
import { convertSeirekiWrap } from '@/utils/wareki';

interface OtherInfoFormProps {
  clinicSite: string;
}

const OtherInfoForm = ({ clinicSite }: OtherInfoFormProps) => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ClinicCreateFormData>();

  const otherInfo = watch('otherInfo');

  const formatDateValue = (val: string | Dayjs | number | null | undefined) => {
    if (!val) return '';
    // Handle YYYYMM numeric format
    if (typeof val === 'number' && /^\d{6}$/.test(String(val))) {
      const d = dayjs(String(val), 'YYYYMM');
      return d.isValid() ? d.format('YYYY-MM-DD') : String(val);
    }
    const d = dayjs(String(val));
    return d.isValid() ? d.format('YYYY-MM-DD') : (val as string);
  };

  const clinicRankOptions = [
    { label: '3%', value: 1 },
    { label: '6%', value: 2 },
  ];

  const isCompanyOptions = [
    { label: '法人', value: true },
    { label: '個人', value: false },
  ];

  return (
    <div>
      <h3 className="text-xl my-4 font-bold">その他情報</h3>
      {clinicSite !== 'upsystem' && (
        <div className="mb-4 grid grid-cols-4 gap-5 items-start">
          <Controller
            name="otherInfo.company_name"
            control={control}
            render={({ field }) => (
              <TextFieldWithLabel
                label="法人名"
                {...field}
                value={field.value ?? ''}
                error={errors.otherInfo?.company_name}
              />
            )}
          />
          <div>
            <AppAdminFormLabel label="会員ランク" />
            <Controller
              name="otherInfo.clinic_rank"
              control={control}
              render={({ field }) => (
                <Radio.Group
                  {...field}
                  options={clinicRankOptions}
                  onChange={e => {
                    field.onChange(e);
                    setValue('otherInfo.clinic_rank', e.target.value, {
                      shouldValidate: true,
                    });
                  }}
                />
              )}
            />
            {/* 会員ランク is optional here: it is no longer synced (BA 2026-08-05, HIT-6373 — Shinsa owns
                会員ランク again, validation and default included), so only the schema's own 1/2 range check can
                reject it. Kept rendering so any such rejection has somewhere to land instead of leaving 更新
                looking like it did nothing at all. */}
            {errors.otherInfo?.clinic_rank && (
              <p className="text-red-500 text-sm mt-1">
                {errors.otherInfo.clinic_rank.message?.toString()}
              </p>
            )}
          </div>
          <Controller
            name="otherInfo.payment_type"
            control={control}
            render={({ field }) => (
              <TextFieldWithLabel
                label="計算書区分"
                {...field}
                value={field.value ?? ''}
                error={errors.otherInfo?.payment_type}
              />
            )}
          />
          <AppAdminFormLabel label="口座振替日 27" />
          <div>
            <AppAdminFormLabel label="開始請求月" />
            <RA2NDatePicker
              className="w-full"
              placeholder=""
              picker="month"
              value={formatDateValue(otherInfo.claim_start_yearmonth)}
              onChange={date => {
                const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                setValue('otherInfo.claim_start_yearmonth', seireki, {
                  shouldValidate: true,
                });
              }}
            />
            {errors.otherInfo?.claim_start_yearmonth && (
              <p className="text-red-500 text-sm mt-1">
                {errors.otherInfo.claim_start_yearmonth.message?.toString()}
              </p>
            )}
          </div>
          <div>
            <AppAdminFormLabel label="最終請求月" />
            <RA2NDatePicker
              className="w-full"
              placeholder=""
              picker="month"
              value={formatDateValue(otherInfo.claim_end_yearmonth)}
              onChange={date => {
                const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                setValue('otherInfo.claim_end_yearmonth', seireki, {
                  shouldValidate: true,
                });
              }}
            />
            {errors.otherInfo?.claim_end_yearmonth && (
              <p className="text-red-500 text-sm mt-1">
                {errors.otherInfo.claim_end_yearmonth.message?.toString()}
              </p>
            )}
          </div>

          <Controller
            name="otherInfo.sales_staff_id"
            control={control}
            render={({ field }) => (
              <TextFieldWithLabel
                label="営業担当者"
                {...field}
                value={field.value ?? ''}
                error={errors.otherInfo?.sales_staff_id}
              />
            )}
          />
          <div>
            <AppAdminFormLabel label="契約区分" />
            <Controller
              name="otherInfo.is_company"
              control={control}
              render={({ field }) => (
                <Radio.Group
                  {...field}
                  options={isCompanyOptions}
                  onChange={e => {
                    field.onChange(e);
                    setValue('otherInfo.is_company', e.target.value, {
                      shouldValidate: true,
                    });
                  }}
                />
              )}
            />
          </div>
          <Controller
            name="otherInfo.monthly_fee"
            control={control}
            render={({ field }) => (
              <TextFieldWithLabel
                label="会費"
                {...field}
                value={field.value ?? ''}
                error={errors.otherInfo?.monthly_fee}
              />
            )}
          />
        </div>
      )}
      <div>
        <AppAdminFormLabel label="備考" />
        <Controller
          name="otherInfo.remarks"
          control={control}
          render={({ field }) => <AppTextArea {...field} value={field.value ?? ''} rows={4} />}
        />
      </div>

      {errors.otherInfo?.remarks && (
        <p className="text-red-500 text-sm mt-1">{errors.otherInfo.remarks.message}</p>
      )}
    </div>
  );
};

export default OtherInfoForm;
